import { useState, useEffect, useRef, useCallback } from "react";
import { useImmer } from "use-immer";
import { client, fileUploadClient } from "../../../../services/axiosClient";
import { getAcceptExtensionsArray, isExtensionValid } from "../utils/fileTypes";
import { scoreSimilarity } from "../utils/queueFolderMatching";

/**
 * Upload queue state, sequential engine, file/folder pickers.
 *
 * Onda 2.1 (mai/2026): otimismo (``updateInputStatusOptimistically``)
 * + ``uploadingInputsRef`` REMOVIDOS. O hook agora pausa o polling do
 * cockpit (``inputsStatePause``) antes de cada upload da fila e
 * retoma com refresh imediato (``inputsStateResume``) ao finalizar
 * cada item — mesmo padrao do ``useInputUpload``. Status do item da
 * fila (success/error/running) e barra de progresso continuam aqui;
 * status do INPUT (pip, attention_label, etc.) vem 100% do backend.
 */
export function useUploadQueue({
  userDetails,
  selectedMonthData,
  inputsStatus,
  inputsStatePause,
  inputsStateResume,
}) {
  const [queueDrawerOpen, setQueueDrawerOpen] = useState(false);
  const [uploadQueueItems, setUploadQueueItems] = useImmer([]);
  const [queueRunning, setQueueRunning] = useState(false);
  const [queuePauseAfterCurrent, setQueuePauseAfterCurrent] = useState(false);
  const [queueIgnoreUploaded, setQueueIgnoreUploaded] = useState(true);
  const queueAbortControllerRef = useRef(null);
  const queueFolderInputRef = useRef(null);
  const queueFileInputRef = useRef(null);
  const queuePendingInputIdRef = useRef(null);
  const [queueNewSelectedInputId, setQueueNewSelectedInputId] = useState(null);
  const [queueSelectResetKey, setQueueSelectResetKey] = useState(0);

  const pauseRef = useRef(inputsStatePause);
  const resumeRef = useRef(inputsStateResume);
  pauseRef.current = inputsStatePause;
  resumeRef.current = inputsStateResume;

  const canRunQueue = useCallback(() => {
    return (
      userDetails?.user_role === "DEV" &&
      selectedMonthData?.status !== "running"
    );
  }, [userDetails?.user_role, selectedMonthData?.status]);

  const isQueueActive =
    queueRunning || uploadQueueItems.some((it) => it.status === "running");

  const addItemToQueue = useCallback(
    (item) => {
      setUploadQueueItems((draft) => {
        draft.push({
          ...item,
          status: "pending",
          progress: 0,
        });
      });
    },
    [setUploadQueueItems],
  );

  const removeItemFromQueue = useCallback(
    (index) => {
      setUploadQueueItems((draft) => {
        draft.splice(index, 1);
      });
    },
    [setUploadQueueItems],
  );

  const moveItemUp = useCallback(
    (index) => {
      if (index <= 0) return;
      setUploadQueueItems((draft) => {
        const tmp = draft[index - 1];
        draft[index - 1] = draft[index];
        draft[index] = tmp;
      });
    },
    [setUploadQueueItems],
  );

  const moveItemDown = useCallback(
    (index) => {
      setUploadQueueItems((draft) => {
        if (index >= draft.length - 1) return;
        const tmp = draft[index + 1];
        draft[index + 1] = draft[index];
        draft[index] = tmp;
      });
    },
    [setUploadQueueItems],
  );

  const clearQueue = useCallback(() => {
    if (isQueueActive) return;
    setUploadQueueItems([]);
  }, [isQueueActive, setUploadQueueItems]);

  useEffect(() => {
    if (!queueRunning) return;
    if (selectedMonthData?.status === "running") {
      setQueueRunning(false);
      return;
    }
    if (uploadQueueItems.some((it) => it.status === "running")) {
      return;
    }
    const idx = uploadQueueItems.findIndex((it) => it.status === "pending");
    if (idx === -1) {
      setQueueRunning(false);
      const allFinished =
        uploadQueueItems.length > 0 &&
        uploadQueueItems.every(
          (it) => it.status === "success" || it.status === "error",
        );
      if (allFinished) {
        setQueueDrawerOpen(false);
      }
      return;
    }
    const item = uploadQueueItems[idx];
    if (!item.file) {
      setUploadQueueItems((draft) => {
        draft[idx].status = "error";
      });
      setQueueRunning(false);
      return;
    }

    setUploadQueueItems((draft) => {
      draft[idx].status = "running";
      draft[idx].progress = 0;
    });

    if (typeof pauseRef.current === "function") pauseRef.current();

    const formData = new FormData();
    formData.append("file", item.file);
    formData.append("input_control_id", item.input_control_id);

    queueAbortControllerRef.current = new AbortController();

    const markError = () => {
      setUploadQueueItems((draft) => {
        if (draft[idx]) draft[idx].status = "error";
      });
      setQueueRunning(false);
      if (typeof resumeRef.current === "function") {
        resumeRef.current({ refreshNow: true });
      }
    };

    client
      .post("api/awake/")
      .then(() =>
        fileUploadClient.post("api/inputs_upload/", formData, {
          onUploadProgress: (ev) => {
            const percent = ev.total
              ? Math.round((ev.loaded * 100) / ev.total)
              : 0;
            setUploadQueueItems((draft) => {
              if (draft[idx]) draft[idx].progress = percent;
            });
          },
          signal: queueAbortControllerRef.current?.signal,
        }),
      )
      .then((response) => {
        if (response.data?.success) {
          setUploadQueueItems((draft) => {
            if (draft[idx]) {
              draft[idx].status = "success";
              draft[idx].progress = 100;
            }
          });
          if (typeof resumeRef.current === "function") {
            resumeRef.current({ refreshNow: true });
          }
          if (queuePauseAfterCurrent) {
            setQueueRunning(false);
          } else {
            setTimeout(() => setQueueRunning(true), 0);
          }
        } else {
          markError();
        }
      })
      .catch(() => {
        markError();
      });
  }, [
    queueRunning,
    uploadQueueItems,
    selectedMonthData,
    queuePauseAfterCurrent,
    setUploadQueueItems,
  ]);

  const handleQueuePickFile = async (selectedId) => {
    queuePendingInputIdRef.current = selectedId;
    setQueueNewSelectedInputId(selectedId);
    const inp = inputsStatus
      .flatMap((g) => g.inputs || [])
      .find((x) => x.input_id == selectedId);
    if (!inp) return;
    const acceptExtensions = getAcceptExtensionsArray(inp.expected_file_type);
    if (window.showOpenFilePicker) {
      try {
        const [handle] = await window.showOpenFilePicker({
          multiple: false,
          excludeAcceptAllOption: true,
          types: [
            {
              description: `Expected ${acceptExtensions.join(", ")}`,
              accept: { "*/*": acceptExtensions },
            },
          ],
        });
        const file = await handle.getFile();
        addItemToQueue({
          input_control_id: inp.input_control_id,
          input_id: inp.input_id,
          input_name: inp.input_name,
          expected_file_type: inp.expected_file_type,
          file,
        });
        setQueueNewSelectedInputId(null);
        setQueueSelectResetKey((k) => k + 1);
        queuePendingInputIdRef.current = null;
      } catch (err) {
        setQueueNewSelectedInputId(null);
        setQueueSelectResetKey((k) => k + 1);
        queuePendingInputIdRef.current = null;
      }
    } else if (queueFileInputRef.current) {
      queueFileInputRef.current.accept = acceptExtensions.join(",");
      const checkCancellation = () => {
        setTimeout(() => {
          if (queuePendingInputIdRef.current === selectedId) {
            const fileInput = queueFileInputRef.current;
            const hasFile =
              fileInput && fileInput.files && fileInput.files.length > 0;
            if (!hasFile) {
              setQueueNewSelectedInputId(null);
              setQueueSelectResetKey((k) => k + 1);
              queuePendingInputIdRef.current = null;
              if (fileInput) fileInput.value = "";
            }
          }
        }, 100);
      };
      window.addEventListener("focus", checkCancellation, { once: true });
      queueFileInputRef.current.click();
    }
  };

  const handleQueueFolderSelected = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    let inputs = inputsStatus
      .flatMap((g) => g.inputs || [])
      .filter((inp) => inp.source === "file_upload");
    if (queueIgnoreUploaded) {
      inputs = inputs.filter((inp) => inp.stages?.data_source !== "success");
    }
    const usedInputIds = new Set(uploadQueueItems.map((it) => it.input_id));

    const allMatches = [];
    for (const f of files) {
      const ext = f.name.substring(f.name.lastIndexOf(".") + 1).toLowerCase();
      const fileNameWithoutExt = f.name.replace(/\.[^.]+$/, "");

      for (let i = 0; i < inputs.length; i++) {
        const inp = inputs[i];
        if (usedInputIds.has(inp.input_id)) continue;

        if (!isExtensionValid(ext, inp.expected_file_type)) continue;

        const labels = [
          inp.input_name?.pt,
          inp.input_name?.en,
          inp.input_name?.es,
        ].filter(Boolean);
        const maxScore = Math.max(
          ...labels.map((lbl) => scoreSimilarity(fileNameWithoutExt, lbl)),
          0,
        );

        if (maxScore > 0) {
          allMatches.push({
            file: f,
            input: inp,
            inputIndex: i,
            score: maxScore,
          });
        }
      }
    }

    allMatches.sort((a, b) => b.score - a.score);

    const fileMatches = [];
    const assignedFiles = new Set();
    const assignedInputs = new Set();

    for (const match of allMatches) {
      if (assignedFiles.has(match.file.name)) continue;
      if (assignedInputs.has(match.input.input_id)) continue;

      assignedFiles.add(match.file.name);
      assignedInputs.add(match.input.input_id);

      fileMatches.push({
        input_control_id: match.input.input_control_id,
        input_id: match.input.input_id,
        input_name: match.input.input_name,
        expected_file_type: match.input.expected_file_type,
        file: match.file,
        status: "pending",
        progress: 0,
        orderIndex: match.inputIndex,
      });
    }

    fileMatches.sort((a, b) => a.orderIndex - b.orderIndex);

    if (fileMatches.length > 0) {
      setUploadQueueItems((draft) => {
        draft.push(
          ...fileMatches.map((m) => {
            // eslint-disable-next-line no-unused-vars -- destructured to exclude from spread
            const { orderIndex, ...item } = m;
            return item;
          }),
        );
      });
    }
    e.target.value = "";
  };

  return {
    queueDrawerOpen,
    setQueueDrawerOpen,
    uploadQueueItems,
    setUploadQueueItems,
    queueRunning,
    setQueueRunning,
    queuePauseAfterCurrent,
    setQueuePauseAfterCurrent,
    queueIgnoreUploaded,
    setQueueIgnoreUploaded,
    queueAbortControllerRef,
    queueFolderInputRef,
    queueFileInputRef,
    queuePendingInputIdRef,
    queueNewSelectedInputId,
    setQueueNewSelectedInputId,
    queueSelectResetKey,
    setQueueSelectResetKey,
    canRunQueue,
    isQueueActive,
    addItemToQueue,
    removeItemFromQueue,
    moveItemUp,
    moveItemDown,
    clearQueue,
    handleQueuePickFile,
    handleQueueFolderSelected,
  };
}
